import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { X, ChevronRight, ChevronLeft, Sparkles, Trophy, Star, CheckCircle, Play, Target, Zap } from "lucide-react";
import useExperiment from "../hooks/useExperiment";

// Tour steps for Job Seekers - Control (basic)
const jobSeekerStepsControl = [
  {
    target: '[data-testid="jobseeker-dashboard"]',
    content: "Welcome to Rekrutor! This is your personal dashboard where you can track your job search progress and discover new opportunities.",
    title: "Your Career Hub",
    placement: "center",
  },
  {
    target: '[data-testid="edit-profile-btn"]',
    content: "Start by building your professional profile. A complete profile increases your visibility to recruiters and improves job match accuracy.",
    title: "Build Your Profile",
    placement: "bottom",
  },
  {
    target: ".stat-card",
    content: "Track your job search statistics at a glance - applications sent, interviews scheduled, and profile completion status.",
    title: "Your Stats",
    placement: "bottom",
  },
  {
    target: '[data-testid="view-all-jobs"]',
    content: "Browse jobs that match your skills and experience. Our AI-powered matching shows you the best opportunities based on your profile.",
    title: "Matching Jobs",
    placement: "top",
  },
  {
    target: '[data-testid="quick-action-insights"]',
    content: "Get AI-powered career insights and recommendations to help you stand out and advance your career.",
    title: "Career Insights",
    placement: "top",
  },
  {
    target: '[data-testid="quick-action-community"]',
    content: "Connect with other professionals, share experiences, and expand your network in our community forum.",
    title: "Join the Community",
    placement: "top",
  },
];

// Tour steps for Job Seekers - Guided Tour variant
const jobSeekerStepsGuidedTour = [
  {
    target: '[data-testid="jobseeker-dashboard"]',
    content: "🎉 Welcome to Rekrutor! Let's take a quick tour to help you get the most out of our platform. This interactive guide will show you all the key features.",
    title: "Welcome to Your Journey!",
    placement: "center",
    variant: "guided",
  },
  {
    target: '[data-testid="edit-profile-btn"]',
    content: "💡 Pro tip: Profiles with photos get 40% more views! Click here to add your professional photo and fill in your experience.",
    title: "First Step: Your Profile",
    placement: "bottom",
    highlight: true,
    variant: "guided",
  },
  {
    target: ".stat-card",
    content: "📊 These cards update in real-time! Watch your stats grow as you apply to jobs and complete your profile.",
    title: "Live Dashboard Stats",
    placement: "bottom",
    variant: "guided",
  },
  {
    target: '[data-testid="view-all-jobs"]',
    content: "🎯 Our AI analyzes thousands of job postings to find the perfect match for your skills. The more you interact, the smarter it gets!",
    title: "AI-Powered Job Matching",
    placement: "top",
    variant: "guided",
  },
  {
    target: '[data-testid="quick-action-insights"]',
    content: "🔮 Get personalized career advice powered by AI. Discover salary trends, skill gaps, and growth opportunities in your field.",
    title: "Career Intelligence",
    placement: "top",
    variant: "guided",
  },
  {
    target: '[data-testid="quick-action-community"]',
    content: "👥 Join discussions, ask questions, and network with professionals in your industry. Your next opportunity could come from a connection!",
    title: "Community & Networking",
    placement: "top",
    variant: "guided",
  },
];

// Tour steps for Job Seekers - Gamified variant
const jobSeekerStepsGamified = [
  {
    target: '[data-testid="jobseeker-dashboard"]',
    content: "🏆 Welcome, Champion! You're about to embark on your career quest. Complete challenges to unlock achievements and boost your profile!",
    title: "Start Your Quest!",
    placement: "center",
    variant: "gamified",
    badge: "newcomer",
  },
  {
    target: '[data-testid="edit-profile-btn"]',
    content: "⭐ CHALLENGE 1: Complete your profile to earn the 'Profile Pro' badge! Profiles with 100% completion get 5x more recruiter views.",
    title: "Challenge: Profile Pro",
    placement: "bottom",
    variant: "gamified",
    points: 100,
    badge: "profile_pro",
  },
  {
    target: ".stat-card",
    content: "📈 Your score tracker! Every application, interview, and connection adds to your Career Score. Top scorers get featured to recruiters!",
    title: "Your Career Score",
    placement: "bottom",
    variant: "gamified",
  },
  {
    target: '[data-testid="view-all-jobs"]',
    content: "🎯 CHALLENGE 2: Apply to 5 matched jobs this week to earn the 'Go-Getter' badge and unlock premium job alerts!",
    title: "Challenge: Go-Getter",
    placement: "top",
    variant: "gamified",
    points: 200,
    badge: "go_getter",
  },
  {
    target: '[data-testid="quick-action-insights"]',
    content: "🧠 BONUS: Complete the AI Career Assessment to get your personalized Career Path and earn 50 bonus points!",
    title: "Bonus: Career Assessment",
    placement: "top",
    variant: "gamified",
    points: 50,
  },
  {
    target: '[data-testid="quick-action-community"]',
    content: "🎊 FINAL CHALLENGE: Introduce yourself in the community to unlock the 'Networker' badge and get 3 days of Premium free!",
    title: "Challenge: Networker",
    placement: "top",
    variant: "gamified",
    points: 150,
    badge: "networker",
    reward: "3 Days Premium Trial",
  },
];

// Tour steps for Recruiters - Control
const recruiterStepsControl = [
  {
    target: '[data-testid="recruiter-dashboard"]',
    content: "Welcome to Rekrutor! This is your recruitment command center where you can manage job postings, track applicants, and find top talent.",
    title: "Your Hiring Hub",
    placement: "center",
  },
  {
    target: '[data-testid="create-job-btn"]',
    content: "Create your first job posting with our AI-powered job creator. It generates ATS-optimized descriptions to attract the best candidates.",
    title: "Post a Job",
    placement: "bottom",
  },
  {
    target: ".stat-card",
    content: "Monitor your recruitment metrics - active job postings, total applicants, and pending interviews all in one place.",
    title: "Recruitment Stats",
    placement: "bottom",
  },
  {
    target: '[data-testid="quick-action-post"]',
    content: "Use our AI Job Creator to generate compelling, ATS-optimized job descriptions in seconds.",
    title: "AI Job Creator",
    placement: "top",
  },
  {
    target: '[data-testid="quick-action-search"]',
    content: "Search our talent database to find candidates that match your requirements. Filter by skills, experience, and location.",
    title: "Find Candidates",
    placement: "top",
  },
  {
    target: '[data-testid="quick-action-community"]',
    content: "Share hiring insights, post industry updates, and connect with other recruiters in our community.",
    title: "Join the Community",
    placement: "top",
  },
];

// Tour steps for Recruiters - Guided Tour
const recruiterStepsGuidedTour = [
  {
    target: '[data-testid="recruiter-dashboard"]',
    content: "🎉 Welcome to Rekrutor! Let's explore how you can find amazing talent faster than ever. This guided tour covers all the essentials.",
    title: "Welcome, Hiring Champion!",
    placement: "center",
    variant: "guided",
  },
  {
    target: '[data-testid="create-job-btn"]',
    content: "💡 Pro tip: Jobs posted with AI-generated descriptions get 60% more qualified applicants! Let's create your first posting.",
    title: "AI-Powered Job Posting",
    placement: "bottom",
    highlight: true,
    variant: "guided",
  },
  {
    target: ".stat-card",
    content: "📊 Real-time recruitment analytics! Track time-to-hire, application quality scores, and more.",
    title: "Live Recruitment Metrics",
    placement: "bottom",
    variant: "guided",
  },
  {
    target: '[data-testid="quick-action-post"]',
    content: "🤖 Our AI analyzes top-performing job posts in your industry to create descriptions that attract the right candidates.",
    title: "Smart Job Creator",
    placement: "top",
    variant: "guided",
  },
  {
    target: '[data-testid="quick-action-search"]',
    content: "🎯 Search 10,000+ pre-screened candidates. Our AI ranks them by match score so you see the best fits first.",
    title: "Talent Discovery",
    placement: "top",
    variant: "guided",
  },
  {
    target: '[data-testid="quick-action-community"]',
    content: "👥 Connect with HR professionals, share hiring tips, and stay updated on recruitment trends.",
    title: "Recruiter Network",
    placement: "top",
    variant: "guided",
  },
];

// Tour steps for Recruiters - Gamified
const recruiterStepsGamified = [
  {
    target: '[data-testid="recruiter-dashboard"]',
    content: "🏆 Welcome to the Talent Arena! Complete hiring challenges to earn badges, climb the leaderboard, and unlock premium features!",
    title: "Enter the Talent Arena!",
    placement: "center",
    variant: "gamified",
    badge: "recruiter_newcomer",
  },
  {
    target: '[data-testid="create-job-btn"]',
    content: "⭐ CHALLENGE 1: Post your first job to earn the 'Job Launcher' badge and 100 Recruiter Points!",
    title: "Challenge: Job Launcher",
    placement: "bottom",
    variant: "gamified",
    points: 100,
    badge: "job_launcher",
  },
  {
    target: ".stat-card",
    content: "📈 Your Hiring Score! Top recruiters get featured placement and exclusive access to premium candidates.",
    title: "Hiring Leaderboard",
    placement: "bottom",
    variant: "gamified",
  },
  {
    target: '[data-testid="quick-action-post"]',
    content: "🎯 CHALLENGE 2: Create 3 AI-optimized job posts this month to earn 'AI Recruiter' badge!",
    title: "Challenge: AI Recruiter",
    placement: "top",
    variant: "gamified",
    points: 200,
    badge: "ai_recruiter",
  },
  {
    target: '[data-testid="quick-action-search"]',
    content: "🧠 BONUS: Complete 10 candidate searches to unlock advanced filters and earn bonus points!",
    title: "Bonus: Power Searcher",
    placement: "top",
    variant: "gamified",
    points: 75,
  },
  {
    target: '[data-testid="quick-action-community"]',
    content: "🎊 FINAL: Share a hiring tip to earn 'Community Leader' badge and get 1 week of Premium features free!",
    title: "Challenge: Community Leader",
    placement: "top",
    variant: "gamified",
    points: 150,
    badge: "community_leader",
    reward: "1 Week Premium Trial",
  },
];

// Storage keys
const TOUR_STORAGE_KEY = "rekrutor_onboarding_completed";
const TOUR_JOBSEEKER_KEY = "rekrutor_tour_jobseeker";
const TOUR_RECRUITER_KEY = "rekrutor_tour_recruiter";

// Tooltip dimensions (approximate)
const TOOLTIP_WIDTH = 384; // max-w-sm = 24rem = 384px
const TOOLTIP_HEIGHT = 280;

// Calculate tooltip position based on target element with viewport boundary checking
const getTooltipPosition = (targetRect, placement) => {
  if (!targetRect) {
    // Center positioning for welcome step
    return {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    };
  }

  const padding = 16;
  const arrowSize = 8;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  
  // Calculate ideal position - use actual pixel values without transform
  let top, left;
  
  switch (placement) {
    case "bottom": {
      top = targetRect.bottom + padding + arrowSize;
      // Center on target, then clamp to ensure tooltip stays in viewport
      const idealCenter = targetRect.left + targetRect.width / 2;
      // Final left position after centering the tooltip
      let finalLeft = idealCenter - TOOLTIP_WIDTH / 2;
      // Clamp to viewport bounds
      finalLeft = Math.max(padding, Math.min(finalLeft, viewportWidth - TOOLTIP_WIDTH - padding));
      left = finalLeft;
      break;
    }
    case "top": {
      top = targetRect.top - padding - arrowSize - TOOLTIP_HEIGHT;
      const idealCenter = targetRect.left + targetRect.width / 2;
      let finalLeft = idealCenter - TOOLTIP_WIDTH / 2;
      finalLeft = Math.max(padding, Math.min(finalLeft, viewportWidth - TOOLTIP_WIDTH - padding));
      left = finalLeft;
      break;
    }
    case "left": {
      const idealCenter = targetRect.top + targetRect.height / 2;
      top = Math.max(padding, Math.min(idealCenter - TOOLTIP_HEIGHT / 2, viewportHeight - TOOLTIP_HEIGHT - padding));
      left = targetRect.left - padding - arrowSize - TOOLTIP_WIDTH;
      // Ensure doesn't go off left edge
      left = Math.max(padding, left);
      break;
    }
    case "right": {
      const idealCenter = targetRect.top + targetRect.height / 2;
      top = Math.max(padding, Math.min(idealCenter - TOOLTIP_HEIGHT / 2, viewportHeight - TOOLTIP_HEIGHT - padding));
      left = targetRect.right + padding + arrowSize;
      // Ensure doesn't go off right edge
      left = Math.min(left, viewportWidth - TOOLTIP_WIDTH - padding);
      break;
    }
    case "center":
    default:
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
  }
  
  // Return without transform - use direct pixel positioning
  return { top, left };
};

// Get arrow styles based on placement
const getArrowStyles = (placement) => {
  const baseStyles = "absolute w-0 h-0 border-solid";
  
  switch (placement) {
    case "bottom":
      return {
        className: baseStyles,
        style: {
          top: -8,
          left: "50%",
          transform: "translateX(-50%)",
          borderWidth: "0 8px 8px 8px",
          borderColor: "transparent transparent #DC2626 transparent",
        },
      };
    case "top":
      return {
        className: baseStyles,
        style: {
          bottom: -8,
          left: "50%",
          transform: "translateX(-50%)",
          borderWidth: "8px 8px 0 8px",
          borderColor: "#DC2626 transparent transparent transparent",
        },
      };
    case "left":
      return {
        className: baseStyles,
        style: {
          right: -8,
          top: "50%",
          transform: "translateY(-50%)",
          borderWidth: "8px 0 8px 8px",
          borderColor: "transparent transparent transparent #DC2626",
        },
      };
    case "right":
      return {
        className: baseStyles,
        style: {
          left: -8,
          top: "50%",
          transform: "translateY(-50%)",
          borderWidth: "8px 8px 8px 0",
          borderColor: "transparent #DC2626 transparent transparent",
        },
      };
    default:
      return null;
  }
};

const OnboardingTour = ({ userRole }) => {
  const { user } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [steps, setSteps] = useState([]);
  const [shouldShowTour, setShouldShowTour] = useState(null); // null = not checked, true/false = checked
  const [totalPoints, setTotalPoints] = useState(0);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const tooltipRef = useRef(null);

  // A/B Testing integration for onboarding flow
  const { 
    variant: experimentVariant, 
    trackConversion,
    isInExperiment,
    loading: experimentLoading 
  } = useExperiment("onboarding");

  // Get steps based on experiment variant
  const getStepsForVariant = useCallback((role, variant) => {
    if (role === "jobseeker") {
      switch (variant) {
        case "guided_tour":
          return jobSeekerStepsGuidedTour;
        case "gamified":
          return jobSeekerStepsGamified;
        default:
          return jobSeekerStepsControl;
      }
    } else {
      // Recruiter
      switch (variant) {
        case "guided_tour":
          return recruiterStepsGuidedTour;
        case "gamified":
          return recruiterStepsGamified;
        default:
          return recruiterStepsControl;
      }
    }
  }, []);

  // Check if user should see the tour (only once when component mounts with valid user)
  useEffect(() => {
    if (shouldShowTour !== null) return; // Already checked
    if (!user || !userRole) return;
    if (experimentLoading) return; // Wait for experiment assignment

    const storageKey = userRole === "jobseeker" ? TOUR_JOBSEEKER_KEY : TOUR_RECRUITER_KEY;
    const hasCompletedTour = localStorage.getItem(storageKey);
    
    console.log("OnboardingTour: Checking if tour should show", { userRole, hasCompletedTour, experimentVariant });
    
    if (!hasCompletedTour) {
      setShouldShowTour(true);
      const tourSteps = getStepsForVariant(userRole, experimentVariant);
      setSteps(tourSteps);
      console.log("OnboardingTour: Will show tour with", tourSteps.length, "steps, variant:", experimentVariant);
    } else {
      setShouldShowTour(false);
    }
  }, [user, userRole, shouldShowTour, experimentVariant, experimentLoading, getStepsForVariant]);

  // Activate tour after a delay when shouldShowTour is true
  useEffect(() => {
    if (shouldShowTour !== true) return;
    if (isActive) return; // Already active
    if (steps.length === 0) return; // No steps set yet

    console.log("OnboardingTour: Setting up activation timer");
    const timer = setTimeout(() => {
      console.log("OnboardingTour: Activating NOW!");
      setIsActive(true);
    }, 1500);

    return () => {
      console.log("OnboardingTour: Clearing activation timer");
      clearTimeout(timer);
    };
  }, [shouldShowTour, isActive, steps.length]);

  // Update target element position
  useEffect(() => {
    if (!isActive || steps.length === 0) return;

    const updateTargetPosition = () => {
      const step = steps[currentStep];
      if (!step) return;

      if (step.placement === "center") {
        setTargetRect(null);
        return;
      }

      const targetElement = document.querySelector(step.target);
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setTargetRect(rect);
        
        // Scroll element into view if needed
        targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        setTargetRect(null);
      }
    };

    updateTargetPosition();
    
    // Update on resize/scroll
    window.addEventListener("resize", updateTargetPosition);
    window.addEventListener("scroll", updateTargetPosition, true);
    
    return () => {
      window.removeEventListener("resize", updateTargetPosition);
      window.removeEventListener("scroll", updateTargetPosition, true);
    };
  }, [isActive, currentStep, steps]);

  const completeTour = useCallback(() => {
    const storageKey = userRole === "jobseeker" ? TOUR_JOBSEEKER_KEY : TOUR_RECRUITER_KEY;
    localStorage.setItem(storageKey, "true");
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
    console.log("OnboardingTour: Tour completed, saving to localStorage and closing");
    
    // Track completion as conversion for A/B test
    if (isInExperiment) {
      trackConversion("onboarding_completed", { 
        variant: experimentVariant,
        steps_viewed: currentStep + 1,
        total_steps: steps.length,
        points_earned: totalPoints,
        badges_earned: earnedBadges
      });
    }
    
    setShouldShowTour(false); // Prevent re-activation
    setIsActive(false);
  }, [userRole, isInExperiment, trackConversion, experimentVariant, currentStep, steps.length, totalPoints, earnedBadges]);

  const handleNext = () => {
    const step = steps[currentStep];
    
    // Track step completion for gamified variant
    if (step?.variant === "gamified") {
      if (step.points) {
        setTotalPoints(prev => prev + step.points);
      }
      if (step.badge) {
        setEarnedBadges(prev => [...prev, step.badge]);
      }
    }
    
    // Track step progress
    if (isInExperiment) {
      trackConversion("onboarding_step_completed", {
        step: currentStep + 1,
        step_title: step?.title,
        variant: experimentVariant
      });
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    completeTour();
  };

  if (!isActive || steps.length === 0) {
    return null;
  }

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isCentered = step.placement === "center";
  const tooltipPosition = getTooltipPosition(targetRect, step.placement);
  const arrowStyles = !isCentered ? getArrowStyles(step.placement) : null;

  return (
    <AnimatePresence>
      {isActive && (
        <>
          {/* Overlay - Semi-transparent visual only; pointer-events:none so underlying UI (especially the highlighted target) stays clickable */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] pointer-events-none"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            data-testid="tour-overlay"
          />

          {/* Spotlight for target element */}
          {targetRect && !isCentered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed z-[9999] pointer-events-none"
              style={{
                top: targetRect.top - 8,
                left: targetRect.left - 8,
                width: targetRect.width + 16,
                height: targetRect.height + 16,
                boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
                border: "2px solid #DC2626",
                borderRadius: "4px",
              }}
              data-testid="tour-spotlight"
            />
          )}

          {/* Tooltip */}
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="fixed z-[10000] bg-obsidian-light border-2 border-crimson p-6 max-w-sm shadow-2xl"
            style={{
              ...tooltipPosition,
              boxShadow: "0 4px 40px rgba(220, 38, 38, 0.4)",
            }}
            data-testid="onboarding-tooltip"
          >
            {/* Arrow */}
            {arrowStyles && (
              <div className={arrowStyles.className} style={arrowStyles.style} />
            )}

            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-2">
                {step.variant === "gamified" ? (
                  <Trophy className="w-5 h-5 text-yellow-400" />
                ) : step.variant === "guided" ? (
                  <Target className="w-5 h-5 text-blue-400" />
                ) : (
                  <Sparkles className="w-5 h-5 text-crimson" />
                )}
                <h3 className="font-heading text-lg font-bold text-crimson">
                  {step.title}
                </h3>
              </div>
              <button
                onClick={handleSkip}
                className="text-gray-500 hover:text-white transition-colors p-1"
                aria-label="Close tour"
                data-testid="tour-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Gamified Points & Badge Display */}
            {step.variant === "gamified" && (step.points || step.badge || step.reward) && (
              <div className="flex flex-wrap gap-2 mb-3">
                {step.points && (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs flex items-center gap-1">
                    <Star className="w-3 h-3" /> +{step.points} Points
                  </span>
                )}
                {step.badge && (
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs flex items-center gap-1">
                    <Trophy className="w-3 h-3" /> {step.badge.replace(/_/g, ' ').toUpperCase()}
                  </span>
                )}
                {step.reward && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs flex items-center gap-1">
                    <Zap className="w-3 h-3" /> {step.reward}
                  </span>
                )}
              </div>
            )}

            {/* Content */}
            <p className="text-gray-300 text-sm mb-5 leading-relaxed">
              {step.content}
            </p>

            {/* Gamified Progress - Total Points */}
            {experimentVariant === "gamified" && totalPoints > 0 && (
              <div className="mb-4 p-2 bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-between">
                <span className="text-yellow-400 text-xs">Total Points:</span>
                <span className="text-yellow-400 font-bold">{totalPoints}</span>
              </div>
            )}

            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-1.5 mb-5">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i === currentStep 
                      ? "bg-crimson w-6" 
                      : i < currentStep 
                        ? "bg-crimson/60" 
                        : "bg-gray-600"
                  }`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-3">
              {/* Skip button */}
              {!isLastStep && (
                <button
                  onClick={handleSkip}
                  className="text-gray-500 hover:text-gray-300 text-xs uppercase tracking-wider transition-colors"
                  data-testid="tour-skip-btn"
                >
                  Skip Tour
                </button>
              )}

              <div className="flex gap-2 ml-auto">
                {/* Back button */}
                {currentStep > 0 && (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-1 px-3 py-2 border border-[#333] text-white hover:border-crimson text-xs uppercase tracking-wider transition-colors"
                    data-testid="tour-back-btn"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    Back
                  </button>
                )}

                {/* Next/Finish button */}
                <button
                  onClick={handleNext}
                  className="flex items-center gap-1 px-4 py-2 bg-crimson hover:bg-crimson-hover text-white text-xs uppercase tracking-wider font-bold transition-colors"
                  data-testid="tour-next-btn"
                >
                  {isLastStep ? "Got it!" : "Next"}
                  {!isLastStep && <ChevronRight className="w-3 h-3" />}
                </button>
              </div>
            </div>

            {/* Step counter */}
            <div className="text-center mt-4">
              <span className="text-gray-500 text-xs">
                Step {currentStep + 1} of {steps.length}
              </span>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Export a function to manually reset the tour (useful for testing)
export const resetOnboardingTour = (role = null) => {
  if (role === "jobseeker" || role === null) {
    localStorage.removeItem(TOUR_JOBSEEKER_KEY);
  }
  if (role === "recruiter" || role === null) {
    localStorage.removeItem(TOUR_RECRUITER_KEY);
  }
  localStorage.removeItem(TOUR_STORAGE_KEY);
};

// Export a function to check tour status
export const hasCompletedTour = (role) => {
  const key = role === "jobseeker" ? TOUR_JOBSEEKER_KEY : TOUR_RECRUITER_KEY;
  return localStorage.getItem(key) === "true";
};

export default OnboardingTour;
